export default {
  id: 'creating-a-strategy',
  title: 'Creating a Strategy',
  content: `## Creating a Strategy

Here is the step-by-step process to build and activate an Algo Flow strategy.

---

## Step 1 — Open Algo Flows

**Profile → Algo Flows → tap New Strategy** (or the + button)

---

## Step 2 — Name the Strategy

Give it a descriptive name that reflects the conditions it uses.

Examples: "RSI Bounce with Volume", "MACD Crossover UPTREND", "20D High Breakout Filter"

A clear name helps you identify which strategy placed an order when reviewing Trade History.

---

## Step 3 — Select Symbols

Choose which NSE stocks this strategy should monitor. You can add multiple symbols — the engine evaluates each one independently every minute.

Recommended: Start with 5–10 stocks you understand well. A strategy spread across 50 stocks with weak conditions will fire too frequently and create noise.

---

## Step 4 — Add Entry Conditions

Add up to **12 entry conditions**. For each condition, choose:
- **Condition type** — RSI, MACD, Volume, Price vs SMA, Scanner signal
- **Operator and value** — e.g., RSI < 35, Volume ratio > 2.0

Choose the logic mode:
- **ALL** — every condition must be true simultaneously (AND logic) — more selective
- **ANY** — at least one condition must be true (OR logic) — fires more frequently

> Start with ALL logic and 2–3 conditions. Too many OR conditions creates a strategy that fires on almost every stock every day, defeating the purpose.

---

## Step 5 — Set Position Size

Define how many shares to buy when the strategy triggers. The position size field accepts a fixed share count.

TradeLab does not automatically calculate position size from your risk profile here — set it manually based on your risk budget per trade.

---

## Step 6 — Set Cooldown Period

The cooldown (in minutes) prevents the strategy from re-entering the same symbol repeatedly after a trigger.

| Cooldown | Use case |
|---|---|
| 60 min | Intraday strategies — prevent re-entry the same morning |
| 1440 min (1 day) | Daily strategies — one trigger per symbol per day |
| 4320 min (3 days) | Swing strategies — avoid overtrading the same stock |

---

## Step 7 — Set Trend Filter (Optional)

You can restrict the strategy to only fire when the stock is in a certain trend:

| Filter | Effect |
|---|---|
| UPTREND only | Only triggers when price is above SMA50 |
| DOWNTREND only | Only triggers when price is below SMA50 |
| ANY | No trend restriction |

---

## Step 8 — Activate

Tap **Activate**. The strategy status changes to ACTIVE and the engine picks it up in its next run cycle.

---

## Editing a Strategy

You can edit conditions, symbols, position size, and cooldown at any time. Changes take effect at the next evaluation cycle (within 1 minute).

To pause without deleting: tap **Pause**. The strategy remains saved and can be reactivated at any time.`,
};
